import { zodResolver } from '@hookform/resolvers/zod';
import { auditReports } from '@mi-front/api-clients';
import { Box, Grid } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { FormContainer, SelectElement, SwitchElement, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { UserSelectField } from '../../../../../../../../../components/users';
import { useDidUpdateEffect } from '../../../../../../../../../core/hooks/useDidUpdateEffect';
import { useWizardWithData } from '../../../../../../../../../core/hooks/WizardWithData';
import { fetchUsers } from '../../../../../../../../admin/api';
import {
  aodReportTypeMessages,
  aodRuleWizardMessages,
  aodRulesMessages,
  triggerMessages,
} from '../../../../../messages';
import { AodRuleWizardValues } from '../../../types';
import { WizardActionsRHF } from '../../actions/WizardActions';
import { AodNotificationSettingsForm } from './AodNotificationSettingsForm';
import { getBasicInfoSchema } from './basic-info-schema';

const reportTypeOptions = [
  { id: auditReports.AodRuleReportTypeEnum.AUDIT_GIP_STATUS },
  { id: auditReports.AodRuleReportTypeEnum.AUDIT_GIP_ALTERNATIVE_REPORT },
];

const triggerOptions = [{ id: 'ORDER_END' }, { id: 'EOW_AND_ORDER_END' }];

export function AodRuleStep_BasicInfo() {
  const { formatMessage, $t } = useIntl();
  const wizard = useWizardWithData<AodRuleWizardValues>();
  const schema = useMemo(() => getBasicInfoSchema((msg) => $t(msg)), [$t]);

  // Fetch users for the managers field
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['users-for-managers'],
    queryFn: () => fetchUsers({ pageSize: 999 }),
  });

  useDidUpdateEffect(() => {
    wizard.setDataOnly({
      reportConfig: null,
      exportOptions: null,
      exportConditions: null,
    });
  }, [wizard.data.basic.reportType]);

  return (
    <FormContainer
      defaultValues={wizard.data.basic}
      resolver={zodResolver(schema)}
      onSuccess={(data) => {
        wizard.setData({
          basic: data,
        });
      }}
    >
      <Box mb={4}>
        <Grid container spacing={2} mb={4}>
          <Grid item xs={4}>
            <SelectElement
              name="reportType"
              label={formatMessage(aodRuleWizardMessages.addReportType)}
              options={reportTypeOptions.map((option) => ({
                id: option.id,
                label: formatMessage(aodReportTypeMessages[option.id]),
              }))}
              fullWidth
              required
            />
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={5} lg={4}>
            <TextFieldElement
              name="name"
              label={formatMessage(aodRulesMessages.name)}
              placeholder={formatMessage(aodRuleWizardMessages.namePlaceholder)}
              fullWidth
              required
              autoComplete="off"
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={4} lg={3}>
            <SelectElement
              name="trigger"
              label={formatMessage(aodRulesMessages.trigger)}
              options={triggerOptions.map((option) => ({
                id: option.id,
                label: formatMessage(triggerMessages[option.id]),
              }))}
              fullWidth
              required
            />
          </Grid>
        </Grid>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <SwitchElement
              name="active"
              label={formatMessage(aodRulesMessages.active)}
              color="success"
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={8} lg={6}>
            <UserSelectField
              name="managers"
              label={formatMessage(aodRuleWizardMessages.managers)}
              users={usersData?.results || []}
              multiple
              loading={usersLoading}
              placeholder={formatMessage(aodRuleWizardMessages.managersPlaceholder)}
              helperText={formatMessage(aodRuleWizardMessages.managersHelp)}
            />
          </Grid>
        </Grid>

        <AodNotificationSettingsForm />
      </Box>

      <WizardActionsRHF />
    </FormContainer>
  );
}
